import { useNavigation } from "@react-navigation/native";
import React from "react";
import { useSelector } from "react-redux";
import { useReviewChangesModal } from "src/hooks/modals";
import { useDebounce } from "src/hooks/ui";
import { includesIn } from "src/util/search";
import { tFilters, tProductStatus, tTableProduct } from "./types";
import { subRowIncludesStatus, subRowIncludesTerm } from "./utils";

export const useTableData = (
  filters: tFilters,
): {
  data: tTableProduct[];
  totals: {
    all: number;
    published: number;
    unpublished: number;
    outOfStock: number;
  };
} => {
  const dTermFilter = useDebounce(filters.term, 500);
  const rProducts = useSelector((state) => state.BrandProductReducer);
  const totalsRef = React.useRef({ all: 0, published: 0, unpublished: 0, outOfStock: 0 });
  const data = React.useMemo(() => {
    totalsRef.current = { all: 0, published: 0, unpublished: 0, outOfStock: 0 };
    return rProducts.reduce<tTableProduct[]>((result, product) => {
      const hasVariants = product.variants.length > 0;
      const status = !product.inventory
        ? tProductStatus.OutOfStock
        : product.availability === "available"
        ? tProductStatus.Published
        : tProductStatus.Unpublished;
      const row: tTableProduct = {
        id: product.uid,
        uid: product.uid,
        name: product.name,
        image: product.images?.[0]?.image_url,
        sku: product.sku,
        status,
        hasVariants,
        hasTester: !!product.tester,
        isVariant: false,
        casepack: product.case_minimum,
        inventory: product.inventory,
        wholesalePrice: product.wholesale_price,
        msrpPrice: product.msrp_price,
        testerPrice: product.tester?.price || 0,
        subRows: product.variants.map((variant) => {
          const variantStatus = !variant.inventory
            ? tProductStatus.OutOfStock
            : variant.purchasable
            ? tProductStatus.Published
            : tProductStatus.Unpublished;
          return {
            id: `${product.uid}_${variant.sku}`,
            uid: product.uid,
            name: `${product.name} ${variant.options.map((option) => option.label).join(" ")}`,
            image: variant.imageUrl,
            sku: variant.sku,
            status: variantStatus,
            hasVariants: false,
            isVariant: true,
            hasTester: !!variant.tester,
            casepack: product.case_minimum,
            inventory: variant.inventory,
            wholesalePrice: variant.wholesalePrice,
            msrpPrice: variant.msrpPrice,
            testerPrice: variant.tester?.price || 0,
          };
        }),
      };
      const statusFilter =
        !filters.status ||
        (filters.status &&
          (hasVariants && row.subRows
            ? subRowIncludesStatus(row.subRows, filters.status)
            : filters.status === row.status));
      const termFilter =
        !dTermFilter ||
        (hasVariants && row.subRows ? subRowIncludesTerm(row.subRows, dTermFilter) : includesIn(row, dTermFilter));

      if (termFilter) {
        if (hasVariants && row.subRows) {
          row.subRows.forEach((subRow) => {
            totalsRef.current = {
              all: totalsRef.current.all + 1,
              published:
                subRow.status === tProductStatus.Published
                  ? totalsRef.current.published + 1
                  : totalsRef.current.published,
              unpublished:
                subRow.status === tProductStatus.Unpublished
                  ? totalsRef.current.unpublished + 1
                  : totalsRef.current.unpublished,
              outOfStock:
                subRow.status === tProductStatus.OutOfStock
                  ? totalsRef.current.outOfStock + 1
                  : totalsRef.current.outOfStock,
            };
          });
        } else {
          totalsRef.current = {
            all: totalsRef.current.all + 1,
            published:
              status === tProductStatus.Published ? totalsRef.current.published + 1 : totalsRef.current.published,
            unpublished:
              status === tProductStatus.Unpublished ? totalsRef.current.unpublished + 1 : totalsRef.current.unpublished,
            outOfStock:
              status === tProductStatus.OutOfStock ? totalsRef.current.outOfStock + 1 : totalsRef.current.outOfStock,
          };
        }
      }

      if (statusFilter && termFilter) {
        result.push(row);
      }

      return result;
    }, []);
  }, [dTermFilter, filters.status, rProducts]);

  return { data, totals: totalsRef.current };
};

export const useReviewChangesBeforeLeave = ({
  enabled,
  confirmAction,
}: {
  enabled: boolean;
  confirmAction: () => unknown | Promise<unknown>;
}): null => {
  const navigation = useNavigation();
  const { openAsync } = useReviewChangesModal();
  const [isLeaving, setLeaving] = React.useState(false);
  const isDiscardingRef = React.useRef(false);
  const isConfirmActionRunned = React.useRef(false);

  React.useEffect(() => {
    navigation.addListener("beforeRemove", async (e) => {
      if (!enabled || isDiscardingRef.current || isConfirmActionRunned.current) return;
      e.preventDefault();
      setLeaving(true);
    });
  }, [enabled, navigation]);

  React.useEffect(() => {
    (async () => {
      if (enabled && isLeaving) {
        setLeaving(false);
        const response = await openAsync();
        if (response.status === "closed") return;
        if (response.data === "discard") {
          isDiscardingRef.current = true;
          navigation.goBack();
          return;
        }

        await confirmAction();

        isConfirmActionRunned.current = true;
      }
    })();
  }, [confirmAction, enabled, isLeaving, navigation, openAsync]);

  return null;
};
